import type { CSSProperties } from 'react';

/** Preferred colors for common Jira types (pie, hours panel, Done-by-type). */
export const TASK_TYPE_COLORS: Record<string, string> = {
  Story: '#63ba3c',
  Bug: '#e5493a',
  Task: '#4bade8',
  'Sub-task': '#4bade8',
  Epic: '#6554c0',
  'Bug (Sub-task)': '#e5493a',
  'Security Issue': '#000000',
  Security: '#000000',
  'UI/UX': '#f59e0b',
};

export const DEFAULT_TYPE_PALETTE = [
  '#8b5cf6',
  '#6366f1',
  '#06b6d4',
  '#10b981',
  '#f59e0b',
  '#ec4899',
  '#14b8a6',
  '#f97316',
];

export function categorizeIssueType(raw: string | undefined | null): string {
  const t = (raw ?? '').toLowerCase();
  if (t.includes('story')) return 'Story';
  if (t === 'bug') return 'Bug';
  return raw?.trim() || 'Unknown';
}

export function isSubTaskStripeLabel(name: string): boolean {
  const lower = name.toLowerCase();
  return lower === 'sub-task' || lower === 'subtask';
}

export function isBugSubtaskStripeLabel(name: string): boolean {
  return name.trim().toLowerCase() === 'bug (sub-task)';
}

export function getTypeColor(name: string, index: number): string {
  if (name in TASK_TYPE_COLORS) return TASK_TYPE_COLORS[name];
  return DEFAULT_TYPE_PALETTE[index % DEFAULT_TYPE_PALETTE.length];
}

export function typeSwatchStyle(name: string, color: string, size = 16): CSSProperties {
  const isSub = isSubTaskStripeLabel(name);
  const isBugSub = isBugSubtaskStripeLabel(name);
  return {
    width: size,
    height: size,
    borderRadius: size <= 12 ? 2 : 4,
    display: 'inline-block',
    flexShrink: 0,
    backgroundColor: isSub || isBugSub ? '#ffffff' : color,
    backgroundImage: isSub
      ? 'repeating-linear-gradient(45deg, #4bade8, #4bade8 2px, #ffffff 2px, #ffffff 4px)'
      : isBugSub
        ? 'repeating-linear-gradient(45deg, #e5493a, #e5493a 2px, #ffffff 2px, #ffffff 4px)'
        : 'none',
    border: isSub ? '1px solid #4bade8' : isBugSub ? '1px solid #e5493a' : 'none',
  };
}

export type TypeAgg = { issueCount: number; hours: number; rowCount: number };

export function aggregateWorklogByIssueType(
  entries: Array<{ type?: string; key?: string; timeSpent?: number }>
): Record<string, TypeAgg> {
  const acc: Record<string, { keys: Set<string>; hours: number; rowCount: number }> = {};
  for (const entry of entries) {
    const cat = categorizeIssueType(entry.type);
    if (!acc[cat]) {
      acc[cat] = { keys: new Set(), hours: 0, rowCount: 0 };
    }
    if (entry.key) acc[cat].keys.add(entry.key);
    acc[cat].hours += entry.timeSpent || 0;
    acc[cat].rowCount += 1;
  }
  const out: Record<string, TypeAgg> = {};
  for (const [name, v] of Object.entries(acc)) {
    out[name] = { issueCount: v.keys.size, hours: v.hours, rowCount: v.rowCount };
  }
  return out;
}

export type PrecomputedTypeRow = {
  type: string;
  rowCount: number;
  hours: number;
  issueCount?: number;
};

export function aggregatePrecomputedByIssueType(
  rows: PrecomputedTypeRow[]
): Record<string, TypeAgg> {
  const acc: Record<string, TypeAgg> = {};
  for (const row of rows) {
    const cat = categorizeIssueType(row.type);
    if (!acc[cat]) {
      acc[cat] = { issueCount: 0, hours: 0, rowCount: 0 };
    }
    acc[cat].issueCount += row.issueCount ?? row.rowCount;
    acc[cat].hours += row.hours;
    acc[cat].rowCount += row.rowCount;
  }
  return acc;
}
